import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type {
  VendorIntegrationListItem,
  VendorIntegrationWithCodeResponse,
} from "@pull/api-contract";
import { VendorIntegrationMode, VendorIntegrationType } from "@pull/shared";
import { DashboardLayout } from "../components/DashboardLayout";
import {
  useCreateWixIntegration,
  useIntegrations,
  useReissueIntegrationCode,
} from "../hooks/useIntegrations";

const MODE_LABELS: Record<VendorIntegrationMode, string> = {
  [VendorIntegrationMode.LIVE]: "Live",
  [VendorIntegrationMode.SANDBOX]: "Sandbox",
  [VendorIntegrationMode.NON_BILLING]: "Non billing",
};

/** The reasons the Wix install callback sends back on a failed install. */
const INSTALL_FAILURE_MESSAGES: Record<string, string> = {
  invalid_request: "The install link was incomplete. Get a new install link and try again.",
  invalid_credentials:
    "The install link has expired or was already used. Get a new install link and try again.",
  site_already_connected: "That Wix site is already connected to another PULL account.",
  wix_unreachable: "Wix did not respond to PULL. Please try again in a few minutes.",
  not_configured: "Wix connections are not available yet. Please contact PULL support.",
};

function formatDate(value: Date | string | null) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function StatusBadge({ connected }: { connected: boolean }) {
  return connected ? (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700">
      <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
      Connected
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
      <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
      Not connected
    </span>
  );
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be refused; the value is still on screen to copy by hand.
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      className="shrink-0 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
    >
      {copied ? "Copied" : label}
    </button>
  );
}

/**
 * A freshly issued code and install link. They exist only in the response that
 * issued them, so this is the one chance to use them — leaving the page loses them.
 */
function IssuedCode({ issued }: { issued: VendorIntegrationWithCodeResponse }) {
  return (
    <div className="mt-5 rounded-lg bg-amber-50 p-4">
      <p className="text-sm font-medium text-gray-900 mb-1">Your install link is ready</p>
      <p className="text-sm text-gray-600 mb-4">
        It works once and is shown only here. If you leave this page, get a new one.
      </p>

      {issued.install_url ? (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <a
            href={issued.install_url}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#F5D020] text-gray-900 text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#d4b31b] transition-colors"
          >
            Install on Wix
          </a>
          <CopyButton value={issued.install_url} label="Copy link" />
        </div>
      ) : (
        <p className="text-sm text-gray-600 mb-4">
          No install link is available because the Wix app is not configured on the PULL API.
        </p>
      )}

      <div>
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
          One-time code
        </p>
        <div className="flex items-center gap-2">
          <code className="flex-1 truncate rounded-lg bg-white px-3 py-2 text-sm text-gray-900">
            {issued.one_time_code}
          </code>
          <CopyButton value={issued.one_time_code} label="Copy code" />
        </div>
      </div>
    </div>
  );
}

function WixIntegrationCard({ integration }: { integration: VendorIntegrationListItem | undefined }) {
  const create = useCreateWixIntegration();
  const reissue = useReissueIntegrationCode();
  const [issued, setIssued] = useState<VendorIntegrationWithCodeResponse | null>(null);

  const isPending = create.isPending || reissue.isPending;
  const error = create.error ?? reissue.error;

  const getInstallLink = () => {
    if (integration) {
      reissue.mutate(VendorIntegrationType.WIX, { onSuccess: setIssued });
    } else {
      create.mutate(undefined, { onSuccess: setIssued });
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Wix</h2>
          <p className="text-sm text-gray-500 mt-1">
            Orders from your Wix store become PULL subscriptions that your customers manage in
            the PULL app.
          </p>
        </div>
        {integration && <StatusBadge connected={integration.connected} />}
      </div>

      {integration?.connected ? (
        <dl className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Site</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {integration.site_url ? (
                <a
                  href={integration.site_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  {integration.site_name ?? integration.site_url}
                </a>
              ) : (
                integration.site_name ?? "—"
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Mode</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {integration.mode ? MODE_LABELS[integration.mode] : "—"}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Connected
            </dt>
            <dd className="mt-1 text-sm text-gray-900">{formatDate(integration.connected_at)}</dd>
          </div>
        </dl>
      ) : (
        <>
          <p className="mt-5 text-sm text-gray-600">
            {integration
              ? "Your Wix integration is not connected. Get an install link to add the PULL app to your Wix site. Any earlier link stops working."
              : "Get an install link to add the PULL app to your Wix site and connect it to PULL."}
          </p>
          <button
            type="button"
            onClick={getInstallLink}
            disabled={isPending}
            className="mt-4 bg-gray-900 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Getting link..." : issued ? "Get a new install link" : "Get install link"}
          </button>
          {error && <p className="mt-3 text-sm text-red-500">{error.message}</p>}
          {issued && <IssuedCode issued={issued} />}
        </>
      )}
    </div>
  );
}

/** Outcome of a Wix install, passed back by the install callback's redirect. */
function InstallOutcome() {
  const [params] = useSearchParams();
  const outcome = params.get("wix");

  if (outcome === "connected") {
    return (
      <div className="mb-6 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-800">
        Your Wix site is connected to PULL.
      </div>
    );
  }

  if (outcome === "failed") {
    const reason = params.get("reason") ?? "";
    return (
      <div className="mb-6 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
        Your Wix site could not be connected.{" "}
        {Object.prototype.hasOwnProperty.call(INSTALL_FAILURE_MESSAGES, reason)
          ? INSTALL_FAILURE_MESSAGES[reason]
          : "Please try again."}
      </div>
    );
  }

  return null;
}

export function Integrations() {
  useEffect(() => { document.title = "Integrations | PayWithPull"; }, []);
  const { data: integrations = [], isLoading, error } = useIntegrations();

  const wix = integrations.find((i) => i.type === VendorIntegrationType.WIX);

  return (
    <DashboardLayout>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Integrations</h1>
      <p className="text-gray-500 mb-8">Connect the platforms you sell through to PULL.</p>

      <div className="max-w-3xl">
        <InstallOutcome />

        {isLoading ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400">
            Loading integrations...
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-red-500">
            {error.message}
          </div>
        ) : (
          <WixIntegrationCard integration={wix} />
        )}
      </div>
    </DashboardLayout>
  );
}
